import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Edit, Trash2, Table, Grid, ChevronUp, ChevronDown, X, Tag as TagIcon } from 'lucide-react';
import { Tag as TagType } from '../../types';
import { passwordManagerAPI } from '../../services/api';
import { useSearch } from '../../contexts/SearchContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import loggingService from '../../services/loggingService';

const Tags: React.FC = () => {
	const { globalSearchTerm } = useSearch();
	const { isDark } = useDarkMode();
	const [tags, setTags] = useState<TagType[]>([]);
	const [showModal, setShowModal] = useState(false);
	const [editingTag, setEditingTag] = useState<TagType | null>(null);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
	const [sortField, setSortField] = useState<string | null>(null);
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
	const hasLoggedPageView = useRef(false);
	const [formData, setFormData] = useState({
		name: '',
		color: '#3b82f6'
	});

	useEffect(() => {
		loadData();
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/password-manager/tags', 'Password Manager', 'Tags');
			hasLoggedPageView.current = true;
		}
	}, []);

	const loadData = async () => {
		try {
			const tagsResponse = await passwordManagerAPI.tags.getAll();
			setTags(tagsResponse.data.data);
		} catch (error) {
			console.error('Error loading data:', error);
		} finally {
			setLoading(false);
		}
	};

	const filteredTags = tags.filter(tag => {
		const matchesSearch = globalSearchTerm === '' || tag.name.toLowerCase().includes(globalSearchTerm.toLowerCase());
		return matchesSearch;
	});

	const sortedTags = [...filteredTags].sort((a, b) => {
		if (!sortField) return 0;
		
		let aValue: any = a[sortField as keyof TagType];
		let bValue: any = b[sortField as keyof TagType];
		
		if (sortField === 'created_at') {
			aValue = new Date(aValue).getTime();
			bValue = new Date(bValue).getTime();
		} else if (typeof aValue === 'string') {
			aValue = aValue.toLowerCase();
			bValue = bValue?.toLowerCase() || '';
		}
		
		if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
		if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
		return 0;
	});

	const handleSort = (field: string) => {
		if (sortField === field) {
			if (sortDirection === 'asc') {
				setSortDirection('desc');
			} else {
				// 3. tıklama: sıralamayı kaldır
				setSortField(null);
				setSortDirection('asc');
			}
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
	};

	const SortIcon = ({ field }: { field: string }) => {
		if (sortField !== field) return null;
		return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />;
	};

	const handleDelete = async (id: number) => {
		if (window.confirm('Are you sure you want to delete this tag?')) {
			try {
				await passwordManagerAPI.tags.delete(id);
				setTags(tags.filter(tag => tag.id !== id));
			} catch (error) {
				console.error('Error deleting tag:', error);
			}
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitting(true);
		try {
			const response = await passwordManagerAPI.tags.create(formData);
			const newTag = response.data.data;
			setTags([...tags, newTag]);
			setShowModal(false);
			setFormData({ name: '', color: '#3b82f6' });
		} catch (error) {
			console.error('Error creating tag:', error);
		} finally {
			setSubmitting(false);
		}
	};

	const handleEditSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!editingTag) return;
		setSubmitting(true);
		try {
			const response = await passwordManagerAPI.tags.update(editingTag.id, formData);
			const updatedTag = response.data.data;
			setTags(tags.map(tag => tag.id === editingTag.id ? updatedTag : tag));
			setShowModal(false);
			setEditingTag(null);
			setFormData({ name: '', color: '#3b82f6' });
		} catch (error) {
			console.error('Error updating tag:', error);
		} finally {
			setSubmitting(false);
		}
	};

	const openEditModal = (tag: TagType) => {
		setEditingTag(tag);
		setFormData({
			name: tag.name,
			color: tag.color
		});
		setShowModal(true);
	};

	const closeModal = () => {
		setShowModal(false);
		setEditingTag(null);
		setFormData({ name: '', color: '#3b82f6' });
	};

	if (loading) {
		return (
			<div className="flex flex-col items-center justify-center h-64 font-mono">
				<div className="w-10 h-10 border-2 border-t-transparent rounded-none animate-spin border-emerald-500 mb-4" />
				<span className="text-[10px] uppercase tracking-[0.2em] text-emerald-500/70 animate-pulse">
					[INITIATING_TAG_BUFFER...]
				</span>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
				<div>
					<h1 className={`text-xl font-mono font-bold uppercase tracking-[0.2em] ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
						[METADATA.TAG_SYSTEM]
					</h1>
					<p className={`text-[10px] font-mono uppercase tracking-widest mt-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
						{filteredTags.length} {filteredTags.length === 1 ? 'IDENTIFIER_ACTIVE' : 'IDENTIFIERS_ACTIVE'}
					</p>
				</div>
				<div className="flex items-center space-x-3">
					<div className="flex items-center space-x-1 border rounded-none p-0.5 overflow-hidden" style={{ borderColor: isDark ? '#222' : '#e5e7eb' }}>
						<button
							onClick={() => setViewMode('table')}
							className={`p-1.5 ${viewMode === 'table' ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-700') : (isDark ? 'text-neutral-600 hover:text-neutral-400' : 'text-neutral-400 hover:text-neutral-600')} transition-all`}
							title="Table View"
						>
							<Table className="w-4 h-4" />
						</button>
						<button
							onClick={() => setViewMode('grid')}
							className={`p-1.5 ${viewMode === 'grid' ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-700') : (isDark ? 'text-neutral-600 hover:text-neutral-400' : 'text-neutral-400 hover:text-neutral-600')} transition-all`}
							title="Grid View"
						>
							<Grid className="w-4 h-4" />
						</button>
					</div>
					<button
						onClick={() => setShowModal(true)}
						className={`flex items-center px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-all rounded-none border ${isDark ? 'border-emerald-500/50 bg-[#111] text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500' : 'border-emerald-600/50 bg-white text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600'}`}
					>
						<Plus className="w-3.5 h-3.5 mr-2" />
						<span>NEW_TAG</span>
					</button>
				</div>
			</div>

			{viewMode === 'table' ? (
				/* Tags Table */
				<div className={`rounded-none overflow-hidden ${isDark ? 'bg-[#0a0a0a] border border-neutral-800' : 'bg-white border border-neutral-200'} shadow-[0_10px_30px_rgba(0,0,0,0.2)]`}>
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead className={`${isDark ? 'bg-[#111]' : 'bg-neutral-50'}`}>
								<tr className={`border-b ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}>
									<th 
										onClick={() => handleSort('name')}
										className={`px-4 py-3 text-left text-[10px] font-mono font-bold ${isDark ? 'text-emerald-500/70' : 'text-emerald-700/70'} uppercase tracking-[0.2em] cursor-pointer ${isDark ? 'hover:bg-emerald-500/5' : 'hover:bg-emerald-500/5'} transition-all`}
									>
										[TAG.IDENTIFIER] <SortIcon field="name" />
									</th>
									<th 
										onClick={() => handleSort('color')}
										className={`px-4 py-3 text-left text-[10px] font-mono font-bold ${isDark ? 'text-emerald-500/70' : 'text-emerald-700/70'} uppercase tracking-[0.2em] cursor-pointer ${isDark ? 'hover:bg-emerald-500/5' : 'hover:bg-emerald-500/5'} transition-all`}
									>
										[TAG.HEX_CODE] <SortIcon field="color" />
									</th>
									<th className={`px-4 py-3 text-right text-[10px] font-mono font-bold ${isDark ? 'text-emerald-500/70' : 'text-emerald-700/70'} uppercase tracking-[0.2em]`}>
										[OP.CMD]
									</th>
								</tr>
							</thead>
							<tbody className={`divide-y ${isDark ? 'divide-neutral-900' : 'divide-neutral-100'}`}>
								{sortedTags.map((tag) => (
								<tr key={tag.id} className={isDark ? 'hover:bg-emerald-500/[0.02] transition-colors' : 'hover:bg-emerald-500/[0.02] transition-colors'}>
									<td className="px-4 py-4">
										<div className="flex items-center space-x-3">
											<div 
												className="w-2 h-2 rounded-none"
												style={{ backgroundColor: tag.color }}
											/>
											<h3 className={`text-xs font-mono font-bold uppercase tracking-wider`} style={{ color: tag.color }}>
												{tag.name}
											</h3>
										</div>
									</td>
									<td className="px-4 py-4">
										<div className="flex items-center space-x-2">
											<span className={`text-[10px] font-mono tracking-widest ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
												{tag.color.toUpperCase()}
											</span>
										</div>
									</td>
									<td className="px-4 py-4">
										<div className="flex items-center justify-end space-x-3">
											<button onClick={() => openEditModal(tag)} className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}>
												<Edit className="w-3.5 h-3.5" />
											</button>
											<button onClick={() => handleDelete(tag.id)} className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-red-500/80' : 'text-neutral-400 hover:text-red-600'}`}>
												<Trash2 className="w-3.5 h-3.5" />
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
			) : (
				/* Tags Grid */
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
					{sortedTags.map((tag) => (
						<div key={tag.id} className={`p-5 rounded-none border transition-all ${isDark ? 'bg-[#0a0a0a] border-neutral-800 hover:border-emerald-500/50' : 'bg-white border-neutral-200 hover:border-emerald-600/50'} shadow-sm hover:shadow-md relative overflow-hidden group`}>
							<div className={`absolute top-0 right-0 w-6 h-6 border-t border-r transition-colors ${isDark ? 'border-neutral-800 group-hover:border-emerald-500/30' : 'border-neutral-200 group-hover:border-emerald-600/30'}`} />
							
							<div className="flex items-center justify-between relative z-10">
								<div className="flex items-center space-x-3">
									<div 
										className="w-3 h-3 rounded-none"
										style={{ backgroundColor: tag.color }}
									/>
									<div>
										<h3 className={`text-sm font-mono font-bold uppercase tracking-wider`} style={{ color: tag.color }}>
											{tag.name}
										</h3>
										<span className={`text-[9px] font-mono tracking-widest uppercase opacity-60 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
											{tag.color.toUpperCase()}
										</span>
									</div>
								</div>
								<div className="flex items-center space-x-1">
									<button onClick={() => openEditModal(tag)} className={`p-1.5 transition-colors border ${isDark ? 'border-neutral-800 hover:border-emerald-500/50 text-neutral-600 hover:text-emerald-400' : 'border-neutral-100 hover:border-emerald-600/50 text-neutral-400 hover:text-emerald-600'}`}>
										<Edit className="w-3.5 h-3.5" />
									</button>
									<button onClick={() => handleDelete(tag.id)} className={`p-1.5 transition-colors border ${isDark ? 'border-neutral-800 hover:border-red-500/30 text-neutral-600 hover:text-red-500' : 'border-neutral-100 hover:border-red-600/30 text-neutral-400 hover:text-red-600'}`}>
										<Trash2 className="w-3.5 h-3.5" />
									</button>
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			{filteredTags.length === 0 && (
				<div className={`text-center py-16 border border-dashed ${isDark ? 'border-neutral-800 bg-neutral-900/20' : 'border-neutral-200 bg-neutral-50'}`}>
					<div className="inline-flex items-center justify-center w-12 h-12 mb-4 border border-neutral-800 rounded-none bg-neutral-900/40">
						<TagIcon className={`w-6 h-6 ${isDark ? 'text-emerald-500/50' : 'text-emerald-700/50'}`} />
					</div>
					<h3 className={`text-sm font-mono font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-emerald-400' : 'text-emerald-800'}`}>
						[METADATA.EMPTY_SET]
					</h3>
					<p className={`text-[10px] font-mono uppercase tracking-widest mb-6 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
						NO_IDENTIFIERS_DEFINED_IN_CURRENT_BUFFER
					</p>
					<button
						onClick={() => setShowModal(true)}
						className={`inline-flex items-center px-6 py-2 font-mono text-[10px] uppercase tracking-widest transition-all rounded-none border ${isDark ? 'border-emerald-500/50 bg-[#111] text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500' : 'border-emerald-600/50 bg-white text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600'}`}
					>
						<Plus className="w-3.5 h-3.5 mr-2" />
						<span>INITIALIZE_IDENTIFIER</span>
					</button>
				</div>
			)}


			{showModal && createPortal(
				<div
					className="fixed inset-0 bg-[#000000cc] backdrop-blur-sm flex items-center justify-center z-[1005] p-4 overflow-y-auto font-mono"
					onClick={() => !submitting && closeModal()}
				>
					<div
						className={`relative w-full max-w-md border rounded-none p-8 overflow-hidden transition-all shadow-[0_0_50px_rgba(0,0,0,0.5)] ${isDark ? 'bg-[#0a0a0a] border-emerald-500/30' : 'bg-white border-emerald-600/30'}`}
						onClick={(e) => e.stopPropagation()}
					>
						{/* FUI Scanning Line */}
						<div className="absolute top-0 left-0 w-full h-[2px] bg-emerald-500/30 shadow-[0_0_15px_#10b981] opacity-20 animate-[scan-move_4s_ease-in-out_infinite]" />
						
						{/* FUI Corner accents */}
						<div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-emerald-500/50" />
						<div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-emerald-500/50" />

						<div className="flex justify-between items-center mb-8">
							<div>
								<h2 className={`text-sm font-bold uppercase tracking-[0.3em] ${isDark ? 'text-emerald-400' : 'text-emerald-800'}`}>
									{editingTag ? '[UPDATE.IDENTIFIER]' : '[INITIALIZE.TAG]'}
								</h2>
								<div className="mt-1 h-[1px] w-12 bg-emerald-500/50" />
							</div>
							<button 
								onClick={closeModal} 
								className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}
							>
								<X className="w-5 h-5" />
							</button>
						</div>

						<form onSubmit={editingTag ? handleEditSubmit : handleSubmit} className="space-y-6">
							<div>
								<label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
									TAG_NAME <span className="text-emerald-500">*</span>
								</label>
								<input
									type="text"
									required
									value={formData.name}
									onChange={(e) => setFormData({ ...formData, name: e.target.value })}
									className={`w-full px-4 py-3 rounded-none border text-xs font-mono transition-all focus:outline-none ${isDark ? 'bg-[#111] border-neutral-800 text-emerald-50 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-200 text-emerald-900 focus:border-emerald-600/50'}`}
									placeholder="e.g. PRODUCTION"
								/>
							</div>
							
							<div>
								<label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
									HEX_COLOR_CODE <span className="text-emerald-500">*</span>
								</label>
								<div className="flex items-center space-x-3">
									<div className="relative w-12 h-10 overflow-hidden border border-neutral-800 shadow-inner">
										<input
											type="color"
											value={formData.color}
											onChange={(e) => setFormData({ ...formData, color: e.target.value })}
											className="absolute -top-1 -left-1 w-[calc(100%+8px)] h-[calc(100%+8px)] cursor-pointer"
										/>
									</div>
									<input
										type="text"
										value={formData.color.toUpperCase()}
										onChange={(e) => setFormData({ ...formData, color: e.target.value })}
										className={`flex-1 px-4 py-3 rounded-none border text-xs font-mono transition-all focus:outline-none tracking-widest ${isDark ? 'bg-[#111] border-neutral-800 text-emerald-50 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-200 text-emerald-900 focus:border-emerald-600/50'}`}
										placeholder="#00FF00"
									/>
								</div>
								
								<div className="mt-4 flex flex-wrap gap-2">
									{['#10b981', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#f59e0b'].map(color => (
										<button
											key={color}
											type="button"
											onClick={() => setFormData({ ...formData, color })}
											className={`w-6 h-6 rounded-none border transition-all ${formData.color.toLowerCase() === color.toLowerCase() ? 'scale-110 border-white shadow-[0_0_10px_rgba(255,255,255,0.3)]' : 'border-transparent hover:scale-105'}`}
											style={{ backgroundColor: color }}
										/>
									))}
								</div>
							</div>

							<div className="flex justify-end space-x-4 pt-4">
								<button
									type="button"
									onClick={closeModal}
									disabled={submitting}
									className={`px-6 py-2 border font-mono text-[10px] uppercase tracking-widest transition-all ${isDark ? 'border-neutral-800 bg-neutral-900/30 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300' : 'border-neutral-200 bg-neutral-50 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600'} ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
								>
									[CANCEL]
								</button>
								<button
									type="submit"
									disabled={submitting}
									className={`px-8 py-2 border font-mono font-bold text-[10px] uppercase tracking-[0.2em] transition-all flex items-center space-x-3 ${isDark ? 'border-emerald-500/50 bg-[#111] text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500' : 'border-emerald-600/50 bg-white text-emerald-800 hover:bg-emerald-50 hover:border-emerald-600'} disabled:opacity-50 disabled:cursor-not-allowed`}
								>
									{submitting && (
										<div className="w-3 h-3 border border-emerald-500 border-t-transparent animate-spin" />
									)}
									<span>{editingTag ? (submitting ? 'EXECUTING...' : '[COMMIT_CHANGES]') : (submitting ? 'EXECUTING...' : '[COMMIT_TAG]')}</span>
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

export default Tags;

